import { ICategoryPageMatch } from "@faire/retailer-shared/categories/ICategoryPageMatch";
import {
  BRAND_VALUE_FILTER_TYPE,
  buildFilterKey,
} from "@faire/retailer-shared/filters/filterKeys";
import { splitFiltersFromQueryParams } from "@faire/retailer-shared/filters/splitFiltersFromQueryParams";
import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import { IPaginationData } from "@faire/web-api/indigofair/data/IPaginationData";
import { IProductFirstCatNavRequest } from "@faire/web-api/indigofair/data/IProductFirstCatNavRequest";
import { IProductFirstCatNavWithLeanProductTileResponse } from "@faire/web-api/indigofair/data/IProductFirstCatNavWithLeanProductTileResponse";
import { ISearchCatNavBannersRequest } from "@faire/web-api/indigofair/data/ISearchCatNavBannersRequest";
import { ISearchRequestReferrer } from "@faire/web-api/indigofair/data/ISearchRequestReferrer";
import { WebApiRequestCancellationError } from "@faire/web-api/WebApiRequestCancellationError";
import { logError } from "@faire/web/common/logging";
import { makeObservable } from "@faire/web/common/makeObservable";
import { parseQueryParams } from "@faire/web/common/query-params/parseQueryParams";
import { QueryParams } from "@faire/web/common/query-params/QueryParams";
import { Location } from "history";
import once from "lodash/once";
import uniq from "lodash/uniq";
import { action, computed, observable, toJS } from "mobx";
import { match } from "react-router-dom";

import { LoggedOutCategoryBannersViewState } from "@faire/retailer/components/Banners/LoggedOutCategoryBanners/LoggedOutCategoryBannersViewState";
import { FilterConsumer } from "@faire/retailer/components/Filter/FilterConsumer";
import { ILeanProductTileWithBrand } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { readBundledData } from "@faire/retailer/lib/bundledData";
import { AppHistory } from "@faire/retailer/packages/core/services/AppHistory";
import { PageLoadEvent } from "@faire/retailer/services/performance/const";
import { PerformanceMarker } from "@faire/retailer/services/performance/PerformanceMarker";
import { getSettingEnableLargerPageSizeOnLoggedOutCategory } from "@faire/retailer/settings/getSettingEnableLargerPageSizeLoggedOutCategory";
import { ProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";

const FULL_LOGGED_OUT_PAGE_SIZE = 37;
const LARGER_FULL_LOGGED_OUT_PAGE_SIZE = 57;

export class LoggedOutMarketplaceBrandSearchModel extends FilterConsumer {
  loggedOutBannersViewState?: LoggedOutCategoryBannersViewState;

  // cat-nav response observables. have been extracted to useLoggedOutCategoryPageCatNavResponse
  @observable
  productFirstSearchResults?: ILeanProductTileWithBrand[];
  @observable
  relatedSearchTerms?: string[];
  @observable
  moreInC3CategoryName?: string;
  @observable
  moreInC3Terms?: string[];
  @observable
  pageCount?: number;
  @observable
  requestId?: string;
  @observable
  brandPageQueryParams: BrandPageQueryObj | undefined;
  @observable
  totalPrimaryResultsCount: string = "";
  @observable
  paginationData?: IPaginationData;
  @observable
  loading: boolean = true;
  @observable
  dataUpdatedAt: number | undefined = undefined;
  @observable
  error: boolean = false;

  @observable
  private pageNumber: number = 0;

  @observable
  private location: Location;
  @observable
  private match: match<ICategoryPageMatch>;

  @observable
  private referrerType?: ISearchRequestReferrer.SearchRequestReferrerType =
    ISearchRequestReferrer.SearchRequestReferrerType
      .SEARCH_REQUEST_REFERRER_TYPE_UNKNOWN;

  private bannersRequest: Promise<void> | undefined;
  private abortController: AbortController | undefined;

  @computed
  private get shouldCallBannersAndCatNavInParallel(): boolean {
    return this.pageNumber === 0;
  }

  constructor(
    private readonly navigationPath: string,
    location: Location,
    match: match<ICategoryPageMatch>
  ) {
    super();
    makeObservable(this);
    this.location = location;
    this.match = match;
    this.loggedOutBannersViewState = new LoggedOutCategoryBannersViewState();
  }

  init = once(() => this.buildFiltersFromUrl());

  @action private setError = (error: boolean) => {
    this.error = error;
  };

  @action updateLocationHistoryAndMatch = (
    location: Location,
    match: match<ICategoryPageMatch>
  ) => {
    this.location = location;
    this.match = match;
  };

  @computed
  private get matchedFilterByBrandValue(): string | undefined {
    // We support the URL suffix /brandValue/:brandValue for SEO purpose
    // this value should be treated as an initial value for filters and the
    // "filters" property from query parameter takes precedence
    //
    // Also we prefer dash to underscore in URL, so need to do a conversion before
    // using the value
    return this.match.params.brandValue?.replace(/-/g, "_");
  }

  @computed
  get paginationPayload(): IPaginationData {
    return IPaginationData.build({
      page_number: this.pageNumber + 1,
      page_size: this.fetchPageSize,
    });
  }

  private getFilterKeys = () => {
    const filterKeys = splitFiltersFromQueryParams(this.parsedSearch) ?? [];
    if (this.matchedFilterByBrandValue) {
      const brandValueFilterKey = buildFilterKey(
        BRAND_VALUE_FILTER_TYPE,
        this.matchedFilterByBrandValue
      );
      if (!filterKeys.includes(brandValueFilterKey)) {
        filterKeys.push(brandValueFilterKey);
      }
    }
    return uniq(filterKeys).sort();
  };

  @computed
  private get parsedSearch(): QueryParams {
    return parseQueryParams(this.location.search);
  }

  private getSearchProductsPayloadForPage = () => {
    return IProductFirstCatNavRequest.build({
      navigation_path: this.navigationPath,
      filter_keys: this.getFilterKeys(),
      container_name: "marketplace_maker_grid",
      pagination_data: {
        ...this.paginationPayload,
        page_number: this.paginationPayload.page_number,
      },

      referrer: ISearchRequestReferrer.build({
        referrer_type:
          this.referrerType ??
          ISearchRequestReferrer.SearchRequestReferrerType
            .SEARCH_REQUEST_REFERRER_TYPE_UNKNOWN,
        referrer_request_id: this.parsedSearch.get("refReqId") ?? undefined,
      }),
    });
  };

  private retrieveProducts = async (abortController: AbortController) => {
    if (!this.shouldCallBannersAndCatNavInParallel) {
      await this.bannersRequest;
    }
    this.productFirstSearchResults = [];
    const searchProductsPayload = toJS(this.getSearchProductsPayloadForPage());
    PerformanceMarker.get().mark(PageLoadEvent.primaryFetchStart);
    const preFetchedData = readBundledData("category_page_products");

    let response = preFetchedData;

    // if there is no preFetchedData, make the request
    if (!preFetchedData) {
      response = await ProductCacheStore.get().fetchProductCatNav(
        searchProductsPayload,
        {
          signal: abortController.signal,
        }
      );
    }
    this.setProductFirstResponseInformation(response);

    if (this.shouldCallBannersAndCatNavInParallel) {
      await this.bannersRequest;
    }
    this.dataUpdatedAt = Date.now();

    PerformanceMarker.get().mark(PageLoadEvent.primaryFetchEnd);
  };

  @action private setProductFirstResponseInformation = (
    response: IProductFirstCatNavWithLeanProductTileResponse | undefined
  ) => {
    if (!response) {
      return;
    }
    if (
      response.pagination_data &&
      this.fetchPageSize < (response.pagination_data.page_size || 0)
    ) {
      response.product_tiles = response.product_tiles.slice(
        0,
        this.fetchPageSize
      );
      response.pagination_data = {
        ...response.pagination_data,
        page_size: this.fetchPageSize,
        page_count: Math.ceil(
          (response.pagination_data.total_results || 0) / this.fetchPageSize
        ),
      };
    }

    this.productFirstSearchResults = response.product_tiles.map(
      (productTile) => {
        const brand =
          response.brand_by_token[productTile.product?.brand_token ?? ""];

        return {
          ...productTile,
          brand,
        };
      }
    );

    this.requestId = response.request_id;
    this.paginationData = response.pagination_data;
    this.pageCount = response.pagination_data?.page_count || 0;
    this.totalPrimaryResultsCount =
      response.formatted_total_primary_results_count ?? "";
    this.brandPageQueryParams = response.brand_page_query_parameters as
      | BrandPageQueryObj
      | undefined;
    this.relatedSearchTerms = response.related_search_terms ?? [];
    this.moreInC3CategoryName = response.more_in_c3?.more_in_c3_category_name;
    this.moreInC3Terms = response.more_in_c3?.more_in_c3_terms ?? [];
  };

  @computed
  private get searchCatNavBannersPayload() {
    return ISearchCatNavBannersRequest.build({
      navigation_path: this.navigationPath,
      filter_keys: this.getFilterKeys(),
    });
  }

  @action
  // Disabling because this method is mocked on the class prototype in unit tests
  // eslint-disable-next-line @faire/prefer-arrow-methods
  async buildFiltersFromUrl() {
    this.loading = true;
    this.setError(false);
    try {
      if (this.abortController) {
        this.abortController.abort();
      }
      const abortController = new AbortController();
      this.abortController = abortController;
      const parsedSearch = parseQueryParams(
        AppHistory.get().history.location.search
      );

      const page = parsedSearch.get("page");
      if (page) {
        this.pageNumber = parseInt(page, 10) - 1;
      } else {
        this.pageNumber = 0;
      }

      this.loggedOutBannersViewState?.setPageNumber(this.pageNumber + 1);
      this.bannersRequest = this.loggedOutBannersViewState?.fetchBanners(
        this.searchCatNavBannersPayload,
        abortController.signal
      );

      this.referrerType =
        ISearchRequestReferrer.SearchRequestReferrerType[
          this.parsedSearch.getOrDefault("refType", "NONE")
        ];
      await this.retrieveProducts(abortController);
    } catch (error) {
      if (!(error instanceof WebApiRequestCancellationError)) {
        logError(error);
        this.setError(true);
      }
    } finally {
      this.loading = false;
    }
  }

  // Though this model does not need these functions, they must still be explicitly defined
  // in order to satisfy the FilterConsumer that this model implements.
  generateUrlFromFilters = () => "";
  @action beforeUrlUpdate = () => {};

  private get fetchPageSize() {
    const isProductPageSizeXP =
      getSettingEnableLargerPageSizeOnLoggedOutCategory();
    return isProductPageSizeXP
      ? LARGER_FULL_LOGGED_OUT_PAGE_SIZE
      : FULL_LOGGED_OUT_PAGE_SIZE;
  }
}
